"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.parentDesignVersion = parentDesignVersion;
exports.parentIdeaVersion = parentIdeaVersion;
exports.weavePlan = weavePlan;
const fs = __importStar(require("fs-extra"));
const path = __importStar(require("path"));
const idUtils_1 = require("../../core/dist/idUtils");
const docNaming_1 = require("../../core/dist/docNaming");
const frontmatterUtils_1 = require("../../core/dist/frontmatterUtils");
const dist_1 = require("../../core/dist");
const req_1 = require("./req");
const thread_1 = require("./thread");
/**
 * The current version (and id) of a thread's design doc — a plan's `design_version`
 * staleness baseline. Returns undefined when the thread has no design (a weave-root
 * plan, or a plan minted before a design exists), so callers fall back to the floor.
 *
 * Mirrors {@link lockedReqVersion}: a downstream creator reads the LIVE parent version
 * at write time instead of hardcoding a constant. Stamping a constant `1` is the bug
 * this exists to prevent — every plan would be born "stale" against a design long past v1.
 */
async function parentDesignVersion(threadPath, threadId, deps) {
    // Dual-read: canonical design.md first, legacy {threadId}-design.md second.
    const designPath = [path.join(threadPath, 'design.md'), path.join(threadPath, `${threadId}-design.md`)]
        .find(p => fs.existsSync(p));
    if (!designPath)
        return undefined;
    const design = (await deps.loadDoc(designPath));
    return { version: design.version, id: design.id };
}
/**
 * The current version (and id) of a thread's idea doc — a design's `idea_version`
 * staleness baseline. Returns undefined when the thread has no idea. Sibling of
 * {@link parentDesignVersion}, one level up the chain (design <- idea).
 */
async function parentIdeaVersion(threadPath, threadId, deps) {
    // Dual-read: canonical idea.md first, legacy {threadId}-idea.md second.
    const ideaPath = [path.join(threadPath, 'idea.md'), path.join(threadPath, `${threadId}-idea.md`)]
        .find(p => fs.existsSync(p));
    if (!ideaPath)
        return undefined;
    const idea = (await deps.loadDoc(ideaPath));
    return { version: idea.version, id: idea.id };
}
/**
 * Tool-call wire markers that must never appear in a plan's `goal`/`title`.
 * When an agent emits a malformed tool call, the harness can shove the raw
 * function-call XML (e.g. `</goal>`, `<parameter name="steps">…`) into a string
 * argument; serialized verbatim it corrupts the plan body. Their presence is a
 * positive signal of a malformed call, never legitimate prose.
 */
const WIRE_MARKER = /<\/?(?:antml:)?(?:goal|parameter|invoke|function_calls)\b/i;
/** Throw if a free-text plan field carries tool-call wire markers (malformed call). */
function assertNoWireLeak(field, value) {
    if (value && WIRE_MARKER.test(value)) {
        throw new Error(`loom_create_plan: "${field}" contains tool-call wire markers ` +
            `(e.g. </goal>, <parameter …>). The call was malformed and would corrupt ` +
            `the plan — re-issue it with clean field values and a structured "steps" array.`);
    }
}
/**
 * Normalize the `steps` argument at the use-case boundary into a validated array.
 * A malformed agent call can deliver `steps` JSON-encoded as a string (cast
 * `as any[]` at the tool layer hides it); coerce a string via JSON.parse, then
 * hard-reject anything that is not an array of step objects with a non-empty
 * `description`. Never silently degrade a non-empty input to `[]`.
 */
function coerceSteps(raw) {
    if (raw === undefined || raw === null)
        return [];
    let steps = raw;
    if (typeof steps === 'string') {
        const trimmed = steps.trim();
        if (trimmed === '')
            return [];
        try {
            steps = JSON.parse(trimmed);
        }
        catch (e) {
            throw new Error(`loom_create_plan: "steps" arrived as an unparseable string, not an array ` +
                `(JSON parse failed: ${e.message}). Pass steps as a structured array.`);
        }
    }
    if (!Array.isArray(steps)) {
        throw new Error(`loom_create_plan: "steps" must be an array of step objects, got ${typeof steps}.`);
    }
    steps.forEach((s, i) => {
        if (typeof s !== 'object' || s === null || Array.isArray(s)) {
            throw new Error(`loom_create_plan: step ${i + 1} is not an object ` +
                `(got ${Array.isArray(s) ? 'array' : typeof s}).`);
        }
        const desc = s.description;
        if (typeof desc !== 'string' || desc.trim() === '') {
            throw new Error(`loom_create_plan: step ${i + 1} is missing a non-empty "description".`);
        }
    });
    return steps;
}
/** Build canonical PlanSteps from structured create input (born frontmatter-native). */
function buildStructuredSteps(stepsInput) {
    const taken = new Set();
    // Pass 1: assign every step's stable id by order — blockedBy resolves against these.
    const withIds = stepsInput.map((s, i) => {
        const description = s.description ?? '';
        const title = (s.title && s.title.trim()) ? s.title.trim() : description;
        return { s, order: i + 1, description, title, id: (0, dist_1.slugifyStepId)(title || description, taken) };
    });
    const orderedStepIds = withIds.map(w => w.id);
    // Pass 2: build steps, normalizing each blockedBy to stable slug ids (ordinals → the
    // id at that position; slugs / plan-ids pass through; out-of-range throws; self-block
    // rejected). Persisting ids (never ordinals) keeps the graph reorder-safe.
    return withIds.map(({ s, order, description, title, id }) => ({
        id,
        order,
        status: 'pending',
        title,
        description,
        files_touched: s.files ?? [],
        blockedBy: (0, dist_1.resolveBlockedByIds)(s.blockedBy, orderedStepIds, id),
        satisfies: s.satisfies ?? [],
        ...(s.detail && s.detail.trim() ? { detail: s.detail } : {}),
    }));
}
async function weavePlan(input, deps) {
    // Validate the agent's input at the use-case boundary so every delivery layer
    // (CLI, MCP, extension) inherits the guard: reject wire-marker body leaks and
    // coerce/validate steps. A corrupt plan must error, never persist + return success.
    assertNoWireLeak('goal', input.goal);
    assertNoWireLeak('title', input.title);
    const steps = coerceSteps(input.steps);
    const weavePath = path.join(deps.loomRoot, 'loom', input.weaveId);
    // Invariant: every doc lives in a thread; a weave folder contains only threads.
    // Weave-root plan creation is retired — a threadId is required.
    if (!input.threadId) {
        throw new Error('Cannot create a plan at weave root: every doc must live in a thread. Pass a threadId (create/select a thread first).');
    }
    {
        const threadPath = path.join(weavePath, input.threadId);
        const plansDir = path.join(threadPath, 'plans');
        await deps.fs.ensureDir(plansDir);
        // Canonical flat plan filename: plan-NNN.md (thread-local ordinal, gaps preserved).
        // nextOrdinal recognises both new (plan-NNN.md) and legacy ({threadId}-plan-NNN.md) names.
        const existingFiles = await deps.fs.readdir(plansDir).catch(() => []);
        const planTitle = input.title || `${input.threadId} Plan`;
        const planFilename = (0, docNaming_1.planFileName)((0, docNaming_1.nextOrdinal)(existingFiles, 'plan'));
        const planId = (0, idUtils_1.generateDocId)('plan');
        // Read the thread design once: it supplies both the parent link (when not
        // given explicitly) and the design_version staleness baseline. Reading the
        // LIVE version here is the fix — a plan must be born current against its design,
        // never stamped a constant 1 (which made every plan a false-positive stale).
        const design = await parentDesignVersion(threadPath, input.threadId, { loadDoc: deps.loadDoc, fs: deps.fs });
        const parentId = input.parentId ?? design?.id ?? null;
        const planSteps = buildStructuredSteps(steps);
        const body = (0, dist_1.serializePlanBody)(planSteps, { goal: input.goal });
        const baseFrontmatter = (0, frontmatterUtils_1.createBaseFrontmatter)('plan', planId, planTitle, parentId);
        // Stamp the locked req version this plan was built against (req-staleness baseline).
        const reqV = await (0, req_1.lockedReqVersion)(deps.loomRoot, input.weaveId, input.threadId, { loadDoc: deps.loadDoc, fs: deps.fs });
        const doc = {
            ...baseFrontmatter,
            type: 'plan',
            status: 'active',
            design_version: design?.version ?? 1,
            target_version: '0.1.0',
            steps: planSteps,
            content: body,
            ...(reqV !== undefined ? { req_version: reqV } : {}),
        };
        // Plans are born frontmatter-native so the saver persists the steps block.
        doc._stepsFromFrontmatter = true;
        const filePath = path.join(plansDir, planFilename);
        await deps.saveDoc(doc, filePath);
        // Auto-scaffold the thread manifest (first-create seam) so the thread is on the roadmap.
        await (0, thread_1.ensureThreadManifest)(input.weaveId, input.threadId, planTitle, {
            getActiveLoomRoot: () => deps.loomRoot,
            saveDoc: deps.saveDoc,
            fs: deps.fs,
        });
        return { id: planId, filePath };
    }
}
